import React, { useState, useEffect, useRef, useCallback } from 'react';

const SCENES = [
  { id: 'peace', name: '平安顺遂', desc: '出入与日常安稳', icon: '🛡️' },
  { id: 'health', name: '身心调护', desc: '作息与情志', icon: '🌿' },
  { id: 'career', name: '事业前程', desc: '工作与成长', icon: '💼' },
  { id: 'wealth', name: '财运丰盈', desc: '规划与心态', icon: '💰' },
  { id: 'love', name: '情缘和美', desc: '人际与亲密', icon: '💕' },
  { id: 'family', name: '家宅和睦', desc: '亲子与长辈', icon: '🏠' },
  { id: 'study', name: '学业进益', desc: '考试与进修', icon: '📚' }
];

const THEMES = [
  { id: 'amber', name: '琥珀金', color: '#D4A574', glow: 'rgba(212, 165, 116, 0.45)' },
  { id: 'jade', name: '青玉', color: '#5D9C8A', glow: 'rgba(93, 156, 138, 0.45)' },
  { id: 'violet', name: '紫气', color: '#8B7FC8', glow: 'rgba(139, 127, 200, 0.45)' },
  { id: 'crimson', name: '丹砂', color: '#C45C5C', glow: 'rgba(196, 92, 92, 0.45)' },
  { id: 'ink', name: '墨色', color: '#6B7B8C', glow: 'rgba(107, 123, 140, 0.4)' },
  { id: 'moon', name: '月白', color: '#B8C5D6', glow: 'rgba(184, 197, 214, 0.5)' }
];

export default function YunxiangPanel({ userId, requestApi, fetchUserInfo, userInfo }) {
  const [step, setStep] = useState(0);
  const [scene, setScene] = useState('peace');
  const [theme, setTheme] = useState('jade');
  const [wish, setWish] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [ritualActive, setRitualActive] = useState(false);
  const [copyTip, setCopyTip] = useState('');
  const [shareTip, setShareTip] = useState('');
  const shareRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const r = await requestApi(`/yunxiang/history/${userId}`);
      if (r.success && Array.isArray(r.history)) setHistory(r.history);
    } catch (_) {
      /* ignore */
    }
  }, [userId, requestApi]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const remaining = userInfo?.freeCalls?.yunxiang;
  const quotaText =
    userInfo?.isVip === true
      ? '会员畅用'
      : remaining === undefined || remaining === null
        ? '—'
        : `${remaining} 次/今日`;

  const runBless = async () => {
    setLoading(true);
    try {
      const r = await requestApi('/yunxiang/bless', {
        method: 'POST',
        data: { userId, scene, theme, wish: wish.trim() }
      });
      if (r.error === 'limit') {
        alert(r.message || '今日次数已用完');
        setStep(0);
        setRitualActive(false);
        return;
      }
      if (r.error && !r.success) {
        alert(r.error || '请求失败');
        setStep(0);
        setRitualActive(false);
        return;
      }
      setResult(r);
      setStep(2);
      loadHistory();
      if (fetchUserInfo) fetchUserInfo();
    } catch (e) {
      console.error(e);
      alert('网络或服务异常，请稍后重试');
      setStep(0);
    } finally {
      setLoading(false);
      setRitualActive(false);
    }
  };

  const startRitual = () => {
    setResult(null);
    setStep(1);
    setRitualActive(true);
    window.setTimeout(() => {
      runBless();
    }, 4200);
  };

  const copyBlessingText = async () => {
    if (!result) return;
    const text = [
      result.title || '云香祈福',
      result.poeticLine || '',
      '',
      result.blessing || '',
      '',
      `场景：${result.sceneLabel || ''}  主题：${result.themeLabel || ''}`,
      result.solarTerm ? `节气：${result.solarTerm}` : '',
      result.lunarStr || '',
      '',
      '行动建议：',
      result.guidance || '',
      '',
      `正念：${result.mindfulness || ''}`,
      '',
      '云端问道 · 文化体验非宗教法事 · 不宣称灵验'
    ].filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopyTip('祈福文案已复制');
      window.setTimeout(() => setCopyTip(''), 2000);
    } catch (_) {
      setCopyTip('复制失败，请手动复制');
      window.setTimeout(() => setCopyTip(''), 2000);
    }
  };

  const createAndCopyShareLink = async () => {
    if (!result) return;
    try {
      const r = await requestApi('/share/create', {
        method: 'POST',
        data: { userId, type: 'yunxiang', payload: result }
      });
      if (!r?.success || !r?.url) throw new Error('share failed');
      await navigator.clipboard.writeText(r.url);
      setShareTip('分享链接已复制');
      window.setTimeout(() => setShareTip(''), 2000);
    } catch (_) {
      setShareTip('生成分享失败，请稍后再试');
      window.setTimeout(() => setShareTip(''), 2000);
    }
  };

  const downloadSharePng = () => {
    if (!result || !shareRef.current) return;
    const canvas = document.createElement('canvas');
    // 手机壁纸尺寸 9:16
    const w = 1080;
    const h = 1920;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const th = THEMES.find((t) => t.id === theme) || THEMES[1];
    const poetic = (result.poeticLine || '').split(/[；;]/).filter(Boolean);

    // 先计算内容总高度
    let contentHeight = 0;
    contentHeight += 70; // 标题高度
    contentHeight += 60; // 诗词高度（每行）
    contentHeight += poetic.length * 60;
    contentHeight += 50; // 祈福文前间距
    // 祈福文行数
    let line = '';
    let blessingLines = 1;
    for (let i = 0; i < (result.blessing || '').length; i += 1) {
      const testLine = line + (result.blessing || '')[i];
      ctx.font = '36px sans-serif';
      if (ctx.measureText(testLine).width > 900 && line) {
        blessingLines++;
        line = (result.blessing || '')[i];
      } else {
        line = testLine;
      }
    }
    contentHeight += blessingLines * 54;
    contentHeight += 40; // 元信息间距
    contentHeight += 50 + (result.solarTerm ? 48 : 0) + 48; // 元信息
    contentHeight += 70; // 分隔线
    contentHeight += 60; // 行动建议前间距
    // 行动建议行数
    line = '';
    let guidanceLines = 1;
    const guidanceText = `行动建议：${result.guidance || ''}`;
    for (let i = 0; i < guidanceText.length; i += 1) {
      const testLine = line + guidanceText[i];
      ctx.font = '30px sans-serif';
      if (ctx.measureText(testLine).width > 920 && line) {
        guidanceLines++;
        line = guidanceText[i];
      } else {
        line = testLine;
      }
    }
    contentHeight += guidanceLines * 48;
    contentHeight += 40; // 正念前间距
    // 正念行数
    line = '';
    let mindfulnessLines = 1;
    const mindfulnessText = `正念：${result.mindfulness || ''}`;
    for (let i = 0; i < mindfulnessText.length; i += 1) {
      const testLine = line + mindfulnessText[i];
      if (ctx.measureText(testLine).width > 920 && line) {
        mindfulnessLines++;
        line = mindfulnessText[i];
      } else {
        line = testLine;
      }
    }
    contentHeight += mindfulnessLines * 48;
    contentHeight += 60; // 底部声明间距

    // 计算起始Y位置，使内容垂直居中（预留上下边框空间）
    const topMargin = 100; // 顶部边框空间
    const bottomMargin = 100; // 底部边框空间
    const availableHeight = h - topMargin - bottomMargin;
    const startY = topMargin + Math.max(0, (availableHeight - contentHeight) / 2);

    // 绘制背景
    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, '#0f0f1a');
    g.addColorStop(0.65, '#1a1a2e');
    g.addColorStop(1, '#141726');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);

    const rg = ctx.createRadialGradient(w * 0.5, h * 0.35, 150, w * 0.5, h * 0.45, 900);
    rg.addColorStop(0, `${th.color}55`);
    rg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = rg;
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 2;
    ctx.strokeRect(60, 60, w - 120, h - 120);
    ctx.strokeStyle = th.color;
    ctx.lineWidth = 4;
    ctx.strokeRect(80, 80, w - 160, h - 160);

    // 从计算好的居中位置开始绘制
    let y = startY;

    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.font = 'bold 56px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('云香祈福', w / 2, y);
    y += 70;

    ctx.font = '32px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.fillText(result.title || '云香祈福', w / 2, y);
    y += 60;

    // 绘制诗词
    ctx.font = '42px serif';
    ctx.fillStyle = 'rgba(255,215,0,0.95)';
    poetic.forEach((lineText) => {
      ctx.fillText(lineText.trim(), w / 2, y);
      y += 60;
    });

    // 绘制祈福文（自动换行）
    y += 50;
    ctx.font = '36px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.textAlign = 'center';
    line = '';
    for (let i = 0; i < (result.blessing || '').length; i += 1) {
      const testLine = line + (result.blessing || '')[i];
      if (ctx.measureText(testLine).width > 900 && line) {
        ctx.fillText(line, w / 2, y);
        y += 54;
        line = (result.blessing || '')[i];
      } else {
        line = testLine;
      }
    }
    if (line) {
      ctx.fillText(line, w / 2, y);
      y += 54;
    }

    // 绘制元信息
    y += 40;
    ctx.font = '28px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillText(`「${result.sceneLabel || ''}」· ${result.themeLabel || ''}`, w / 2, y);
    y += 50;
    if (result.solarTerm) {
      ctx.fillText(`节气：${result.solarTerm}`, w / 2, y);
      y += 48;
    }
    ctx.fillText(result.lunarStr || '', w / 2, y);
    y += 70;

    // 分隔线
    ctx.fillStyle = th.color;
    ctx.fillRect(180, y, w - 360, 2);
    y += 60;

    // 行动建议
    ctx.font = '30px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.textAlign = 'center';
    line = '';
    for (let i = 0; i < guidanceText.length; i += 1) {
      const testLine = line + guidanceText[i];
      if (ctx.measureText(testLine).width > 920 && line) {
        ctx.fillText(line, w / 2, y);
        y += 48;
        line = guidanceText[i];
      } else {
        line = testLine;
      }
    }
    if (line) {
      ctx.fillText(line, w / 2, y);
      y += 48;
    }

    // 正念
    y += 40;
    line = '';
    for (let i = 0; i < mindfulnessText.length; i += 1) {
      const testLine = line + mindfulnessText[i];
      if (ctx.measureText(testLine).width > 920 && line) {
        ctx.fillText(line, w / 2, y);
        y += 48;
        line = mindfulnessText[i];
      } else {
        line = testLine;
      }
    }
    if (line) {
      ctx.fillText(line, w / 2, y);
      y += 48;
    }

    // 底部声明
    ctx.font = '24px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.fillText('云端问道 · 文化体验非宗教法事 · 不宣称灵验', w / 2, h - 120);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `云香祈福-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    }, 'image/png');
  };

  return (
    <div className="panel yunxiang-panel">
      <h2 className="panel-title">🕯️ 云香祈福</h2>
      <p className="panel-subtitle">数字化心香仪式 · AI 结合节令意象生成祈福文与正念提醒</p>
      <p className="panel-subtitle yunxiang-disclaimer">
        本功能为文化体验与情绪陪伴，非宗教法事，不宣称灵验、改运或现实效力；重要决策与身体不适请咨询专业人士。
      </p>
      <div className="yunxiang-quota">
        <span>今日剩余：{quotaText}</span>
        {history.length > 0 && (
          <button type="button" className="yunxiang-history-toggle" onClick={() => setShowHistory(!showHistory)}>
            {showHistory ? '收起记录' : `心愿记录 (${history.length})`}
          </button>
        )}
      </div>

      {showHistory && (
        <div className="yunxiang-history-list">
          {history.slice(0, 12).map((h) => (
            <button
              type="button"
              key={h.id}
              className="yunxiang-history-item"
              onClick={() => {
                setResult(h);
                setScene(h.scene);
                setTheme(h.theme);
                setWish(h.wish || '');
                setStep(2);
              }}
            >
              <span className="yunxiang-history-title">{h.title}</span>
              <span className="yunxiang-history-meta">
                {(h.sceneLabel || '') + (h.themeLabel ? ` · ${h.themeLabel}` : '')}
                {' · '}
                {new Date(h.createdAt).toLocaleString('zh-CN')}
              </span>
            </button>
          ))}
        </div>
      )}

      {step === 0 && (
        <div className="yunxiang-setup">
          <div className="yunxiang-setup-grid">
            <div className="yunxiang-setup-col">
              <div className="form-group">
                <label className="form-label">祈福场景</label>
                <div className="yunxiang-scene-grid">
                  {SCENES.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={`yunxiang-scene-card ${scene === s.id ? 'active' : ''}`}
                      onClick={() => setScene(s.id)}
                    >
                      <span className="yunxiang-scene-icon">{s.icon}</span>
                      <span className="yunxiang-scene-name">{s.name}</span>
                      <span className="yunxiang-scene-desc">{s.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="yunxiang-setup-col">
              <div className="yunxiang-compose-head">
                <div className="yunxiang-compose-title">心香主色</div>
                <div className="yunxiang-compose-sub">主题意象 + 一句话心愿，生成更贴合你的祈福文案</div>
              </div>
              <div className="form-group">
                <label className="form-label">主题色（影响意象）</label>
                <div className="yunxiang-theme-row">
                  {THEMES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      className={`yunxiang-theme-dot ${theme === t.id ? 'active' : ''}`}
                      style={{ '--yx-color': t.color, '--yx-glow': t.glow }}
                      title={t.name}
                      onClick={() => setTheme(t.id)}
                    >
                      <span className="yunxiang-theme-dot__swatch" aria-hidden="true" />
                      <span className="yunxiang-theme-dot__name">{t.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">心愿（选填，最多 200 字）</label>
                <textarea
                  className="form-input"
                  rows={6}
                  maxLength={200}
                  placeholder="写下一句心愿或自我期许，用于 AI 个性化文案（非功效承诺）"
                  value={wish}
                  onChange={(e) => setWish(e.target.value)}
                />
              </div>

              <button
                type="button"
                className="btn-primary btn-large yunxiang-start-btn"
                disabled={loading}
                onClick={startRitual}
              >
                {loading ? '祈福中…' : '开始敬香 · 云香祈福'}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="yunxiang-ritual">
          <div
            className={`yunxiang-ritual-stage ${ritualActive ? 'yunxiang-ritual-stage--active' : ''}`}
            style={{
              '--yx-theme': (THEMES.find((t) => t.id === theme) || THEMES[1]).color,
              '--yx-glow': (THEMES.find((t) => t.id === theme) || THEMES[1]).glow
            }}
          >
            <div className="yunxiang-smoke">
              {[...Array(6)].map((_, i) => (
                <span key={i} className={`yunxiang-smoke-puff yunxiang-smoke-puff--${i}`} />
              ))}
            </div>
            <div className="yunxiang-censer">🕯️</div>
            <p className="yunxiang-ritual-hint">心香袅袅，意念澄明…</p>
            {loading && <p className="yunxiang-ritual-loading">正在生成祈福文…</p>}
          </div>
        </div>
      )}

      {step === 2 && result && (
        <div className="yunxiang-result" ref={shareRef}>
          <div className="yunxiang-result-card">
            <h3 className="yunxiang-result-title">{result.title}</h3>
            <p className="yunxiang-result-poetic">{result.poeticLine}</p>
            <div className="yunxiang-result-bless">{result.blessing}</div>
            <div className="yunxiang-result-meta">
              <span>场景：{result.sceneLabel}</span>
              <span>主题：{result.themeLabel}</span>
              {result.solarTerm ? <span>节气：{result.solarTerm}</span> : null}
            </div>
            <p className="yunxiang-result-lunar">{result.lunarStr}</p>
            <div className="yunxiang-result-section">
              <strong>行动建议</strong>
              <p>{result.guidance}</p>
            </div>
            <div className="yunxiang-result-mindfulness">
              <span>正念</span>
              {result.mindfulness}
            </div>
          </div>
          <div className="yunxiang-result-actions">
            <button type="button" className="btn-primary" onClick={downloadSharePng}>
              下载祈福卡 PNG
            </button>
            <button type="button" className="btn-secondary" onClick={createAndCopyShareLink}>
              生成分享链接
            </button>
            <button type="button" className="btn-secondary yunxiang-copy-btn" onClick={copyBlessingText}>
              复制祈福文案
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setStep(0);
                setResult(null);
              }}
            >
              再祈一次
            </button>
          </div>
          {copyTip ? <p className="yunxiang-copy-tip">{copyTip}</p> : null}
          {shareTip ? <p className="yunxiang-copy-tip">{shareTip}</p> : null}
        </div>
      )}
    </div>
  );
}
